import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import useAuthStore from './store/auth-store'
import { ProtectedRoute } from './components/ProtectedRoute'
import { MainLayout } from './layout/MainLayout'
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { ProjectsPage } from './pages/ProjectsPage'
import { DevicesPage } from './pages/DevicesPage'
import { DeviceDetailPage } from './pages/DeviceDetailPage'
import { ReportsPage } from './pages/ReportsPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import SetupTwoFactorPage from './pages/SetupTwoFactorPage'
import { AccountPage } from './pages/AccountPage'
import PlansPage from './pages/PlansPage'
import StripeSettingsPage from './pages/StripeSettingsPage'
import { AlarmsPage } from './pages/AlarmsPage'
import HelpCenterPage from './pages/HelpCenterPage'
import MessageCenterPage from './pages/MessageCenterPage'
import PublicPortalPage from './pages/PublicPortalPage'
import AppletEmbedPage from './pages/AppletEmbedPage'
import TermsPage from './pages/TermsPage'
import CmsPagesPage from './pages/CmsPagesPage'
import CmsContentPage from './pages/CmsContentPage'

export function AppRoutes() {
  const { fetchCurrentUser, isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentUser()
    }
  }, [isAuthenticated])

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/project/:projectId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProjectsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DevicesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DeviceDetailPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ReportsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/alarms"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AlarmsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Help Center — public marketing-site page. Self-renders its own
            Navigation + Footer; intentionally not wrapped in MainLayout
            or ProtectedRoute so the link works for logged-out visitors. */}
        <Route path="/help" element={<HelpCenterPage />} />
        {/* Terms & Conditions — public legal page linked from the footer's
            Policy column. Self-renders its own Navigation + Footer; outside
            ProtectedRoute so logged-out visitors can read it. */}
        <Route path="/terms" element={<TermsPage />} />
        {/* Message Center — authenticated, self-renders its own chrome
            (Navigation + AppHeader). Both /messages and /messages/<projectId>
            resolve so the user can deep-link to a specific project's feed
            without losing their current project context. */}
        <Route
          path="/messages"
          element={<ProtectedRoute><MessageCenterPage /></ProtectedRoute>}
        />
        <Route
          path="/messages/:projectId"
          element={<ProtectedRoute><MessageCenterPage /></ProtectedRoute>}
        />
        {/* Public Portal — unauthenticated viewer for /public/:projectId
            (and /public/:projectId#gallery). Must sit BEFORE the "*"
            fallback below or React Router redirects it to the homepage. */}
        <Route path="/public/:projectId" element={<PublicPortalPage />} />
        {/* Embedded Data Applet — unauthenticated iframe targets for the
            snippets generated in Settings → Embedded Data Applet. Both
            /text/ and /html/ resolve so neither snippet 404s; they share
            one public data endpoint and render the stylized applet card.
            Intentionally NOT wrapped in ProtectedRoute — these are meant
            to be embedded on third-party sites with no login. Must sit
            BEFORE the "*" fallback below. */}
        <Route path="/project/applet/text/:projectId" element={<AppletEmbedPage variant="text" />} />
        <Route path="/project/applet/html/:projectId" element={<AppletEmbedPage variant="html" />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/setup-2fa" element={<SetupTwoFactorPage />} />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AccountPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Account → View Plans link — per-project plan detail page.
            Self-renders its own Navigation + AppHeader (like Help /
            Message Center pages), so intentionally NOT wrapped in
            MainLayout to avoid double headers. */}
        <Route
          path="/plans"
          element={<ProtectedRoute><PlansPage /></ProtectedRoute>}
        />
        {/* Super-admin only surface — the backend enforces the ROLE_SUPER_ADMIN
            gate, so if a non-admin lands here they'll see the "forbidden"
            response inline. Self-renders its own Navigation + AppHeader.
            Note: nginx proxies /admin/* to Django (legacy admin), so this
            route MUST NOT start with /admin — otherwise the React SPA
            never sees the request. */}
        <Route
          path="/stripe-settings"
          element={<ProtectedRoute><StripeSettingsPage /></ProtectedRoute>}
        />
        {/* CMS — in-app content manager (authenticated). NOT under /admin,
            which nginx proxies to the legacy Django admin (see note above). */}
        <Route
          path="/cms-pages"
          element={<ProtectedRoute><CmsPagesPage /></ProtectedRoute>}
        />
        {/* Public CMS content page by slug. Self-renders Navigation + Footer;
            outside ProtectedRoute so logged-out visitors can read it. Must sit
            BEFORE the "*" fallback below. */}
        <Route path="/page/:slug" element={<CmsContentPage />} />
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}
